import React, { useEffect, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { ActionRequiredCard } from '../ActionRequiredCard';
import { useMppCheckTinQuery } from '../../../../../common/hooks/useMppCheckTinQuery';
import { useMppAttestationDataByTin } from '../../../../../common/hooks/useMppAttestationDataByTin';
import { useMppAttestationTypeQuery } from '../../../../../common/hooks/useMppAttestationTypeQuery';
import { buildSection } from '../helpers';

export const MppSection = ({
  user,
  provider,
  onlyShowRequired,
  failedCallsCountIncrease,
  setIsMppLoading,
  setCountsBySections,
}) => {
  const [mppSection, setMppSection] = useState([]);
  const uiTimeNow = moment().format();
  const [isTinMppDeployed, setIsTinMppDeployed] = useState(false);
  const [checkMppAttestationType, setCheckMppAttestationType] = useState(false);
  const [isMppAttestationOnly, setIsMppAttestationOnly] = useState(false);
  const [checkTinContractDate, setCheckTinContractDate] = useState();
  const [, mppCheckTin] = useMppCheckTinQuery({
    onCompleted: (response) => {
      if (response && response.mppCheckTin) {
        const match = response.mppCheckTin.find(
          (item) => item.tin === provider.taxIdNumber
        );
        const date = match.contractEffectiveDate.split(' ');
        const [year, month, day] = date[0].split('-');
        setCheckTinContractDate(`${month}/${day}/${year}`);
        setIsTinMppDeployed(true);
      } else {
        setIsTinMppDeployed(false);
      }
      setCountsBySections((prevSections) => ({
        ...prevSections,
        mpp: { error: false },
      }));
      setIsMppLoading(false);
    },
    onError: (error) => {
      setCountsBySections((prevSections) => ({
        ...prevSections,
        mpp: { error: true },
      }));
      setIsTinMppDeployed(false);
      setCheckTinContractDate(null);

      setIsMppLoading(false);
    },
  });

  const [, getMppAttestationDataByTin] = useMppAttestationDataByTin({
    onCompleted: (response) => {
      let mppCount = 0;

      // eslint-disable-next-line no-unused-expressions
      response?.tins?.forEach((item) => {
        if (item.tin === provider.taxIdNumber) {
          let attestedDate = null;
          const useContractDate =
            !item.rosterAttestedDate &&
            !item.rosterDoNotAttestDate &&
            !item.doNotAttest;

          const useRosterDoNotAttestDate =
            (item.rosterDoNotAttestDate && item.doNotAttest) ||
            (item.rosterDoNotAttestDate &&
              !item.rosterAttestedDate &&
              !item.doNotAttest);

          const useRosterAttestedDate =
            (item.rosterAttestedDate && !item.doNotAttest) ||
            (item.rosterAttestedDate &&
              !item.rosterDoNotAttestDate &&
              !item.doNotAttest);

          if (useContractDate) {
            // If contractDate is null, use contractEffectiveDate from mppCheckTin call
            if (!item.contractDate) {
              attestedDate = checkTinContractDate;
            } else {
              attestedDate = item.contractDate;
            }
          } else if (useRosterDoNotAttestDate) {
            attestedDate = item.rosterDoNotAttestDate;
          } else if (useRosterAttestedDate) {
            attestedDate = item.rosterAttestedDate;
          }

          const attestationDate = moment(attestedDate, 'MM/DD/YYYY').add(
            90,
            'days'
          );

          const attDays = attestationDate.diff(uiTimeNow, 'days');

          mppCount = attDays;
        }
      });

      const mapped = [
        {
          sectionName: 'My Practice Profile',
          daysToAttest: mppCount,
          success: true,
        },
      ].map((section) => buildSection(section));

      setMppSection(mapped);
      setCheckMppAttestationType(true);
    },
    onError: () => {
      failedCallsCountIncrease();
      const mapped = [
        {
          sectionName: 'My Practice Profile',
          success: false,
        },
      ].map((section) => buildSection(section));
      setMppSection(mapped);
      setCheckMppAttestationType(true);

      setIsMppLoading(false);
    },
  });

  const [, getMppAttestationType] = useMppAttestationTypeQuery({
    onCompleted: (response) => {
      setIsMppAttestationOnly(response.mppAttestation);
      setCheckMppAttestationType(false);
    },
    onError: (error) => {
      setCheckMppAttestationType(false);

      setIsMppLoading(false);
    },
  });

  useEffect(() => {
    if (provider?.taxIdNumber && user.uuid) {
      setIsTinMppDeployed(false);
      setIsMppAttestationOnly(false);
      setCheckTinContractDate(null);

      const mppPermissions =
        provider?.permissionCode.includes('PPF') || provider?.allPermission;
      if (mppPermissions) {
        setIsMppLoading(true);
        mppCheckTin({
          variables: {
            tin: provider.taxIdNumber,
          },
        });
      }
    }
  }, [provider?.taxIdNumber]);

  useEffect(() => {
    if (isTinMppDeployed) {
      getMppAttestationDataByTin({
        variables: {
          tin: provider.taxIdNumber,
        },
      });
    }
  }, [isTinMppDeployed]);

  useEffect(() => {
    if (checkMppAttestationType) {
      getMppAttestationType({
        variables: {
          tin: provider.taxIdNumber,
        },
      });
    }
  }, [checkMppAttestationType]);

  return (
    <React.Fragment>
      <If condition={isMppAttestationOnly}>
        <ActionRequiredCard sections={onlyShowRequired ? [] : mppSection} />
      </If>
    </React.Fragment>
  );
};

MppSection.propTypes = {
  user: {
    uuid: PropTypes.string,
  },
  provider: {
    taxIdNumber: PropTypes.string,
  },
  onlyShowRequired: PropTypes.bool,
  failedCallsCountIncrease: PropTypes.func,
  setIsMppLoading: PropTypes.func,
  setCountsBySections: PropTypes.func,
};

MppSection.defaultProps = {
  user: {
    uuid: '',
  },
  provider: {
    taxIdNumber: '',
  },
  onlyShowRequired: false,
  failedCallsCountIncrease: () => {},
  setIsMppLoading: () => {},
  setCountsBySections: () => {},
};
